import { trackChatWithFeedbackEvents } from '@modules/chatWithFeedback/utils';
import { useIsChatWithFeedbackEnabled } from '@modules/feed/hooks';
import { AssistanceResponseYesButton } from '@modules/oracle/components/AssistanceResponseYesButton';
import { FeedbackListSummaryNoButton } from '@modules/oracle/components/FeedbackListSummaryFeedbackButtons/FeedbackListSummaryNoButton';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { useFlagFeedbackSummary } from '@modules/oracle/hooks';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useToggle } from 'react-use';

interface FeedbackListSummaryFeedbackButtonsProps {
  responseID: string;
  isFeedbackSidebarView?: boolean;
  viewToken: string | undefined;
}

export function FeedbackListSummaryFeedbackButtons({
  responseID,
  isFeedbackSidebarView,
  viewToken,
}: FeedbackListSummaryFeedbackButtonsProps) {
  const dispatch = useFeedbackSummaryStore(({ dispatch }) => dispatch);
  const likeSet = useFeedbackSummaryStore(({ visual }) => visual.likeSet);
  const sessionID = useFeedbackSummaryStore(({ data }) => data.sessionID);
  const isResponseYes = likeSet.has(responseID);
  const dislikeSet = useFeedbackSummaryStore(({ visual }) => visual.dislikeSet);
  const isResponseNo = dislikeSet.has(responseID);
  const [flagFeedbackSummary] = useFlagFeedbackSummary();
  const [shouldPlayResponseYesAnimation, toggleShouldPlayResponseYesAnimation] =
    useToggle(false);

  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const onThumbUpButtonClick = async () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    await flagFeedbackSummary({
      isResponseGood: true,
      responseID,
      viewToken,
      sessionID: sessionID ?? undefined,
    });

    toggleShouldPlayResponseYesAnimation();

    dispatch({
      type: FeedbackSummaryActionType.SET_VISUAL,
      payload: {
        likeSet: new Set([...likeSet, responseID]),
      },
    });

    animationTimeoutRef.current = setTimeout(() => {
      toggleShouldPlayResponseYesAnimation();
    }, 5 * 1000); // the animation will be there for 5 seconds

    trackChatWithFeedbackEvents({
      eventName: 'Chat With Feedback - Thumbs Up Button Clicked',
      route: window.location.pathname,
      isFeedbackSidebarView,
    });
  };

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();

  const yesResponseButton = isResponseNo ? null : (
    <AssistanceResponseYesButton
      responseID={responseID}
      onClick={onThumbUpButtonClick}
      disabled={isResponseYes}
      sx={{
        color: isChatWithFeedbackEnabled ? 'primary.main' : 'text.secondary',
      }}
      shouldShowSpark={shouldPlayResponseYesAnimation}
      size="small"
    />
  );

  const noResponseButton = isResponseYes ? null : (
    <Box>
      <Collapse in={!shouldPlayResponseYesAnimation} orientation="horizontal">
        <FeedbackListSummaryNoButton
          responseID={responseID}
          isFeedbackSidebarView={isFeedbackSidebarView}
          viewToken={viewToken}
        />
      </Collapse>
    </Box>
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        Is this helpful?
      </Typography>
      {yesResponseButton}
      {noResponseButton}
    </Stack>
  );
}
